// apps/web/src/features/commercial-proposals/components/CommercialProposalCalcPanel.tsx
//
// @aiPurpose render proposal totals (#1198). Accepts EITHER the live JS
//   preview (while editing a draft) or the backend-authoritative
//   `CommercialProposalCalculationOutput` (after save / for a published
//   proposal). The two render through the same formatters so a saved draft
//   reconciles visually to the live preview it replaced.
// @aiInvariant the live preview is for editing only; the backend calculation
//   is authoritative and is shown for any saved/published view.
import { useTranslation } from 'react-i18next';
import type { CommercialProposalCalculationOutput } from '@magimp/shared';

import { formatCny, formatRub, formatUsd } from '../format.js';
import type { AppLocale } from '../../../i18n/index.js';
import type { CommercialProposalLiveCalc } from '../calc.js';

interface CalcValues {
  serviceCny: number | string;
  totalCny: number | string;
  buyRub: number | string;
  transportUsd: number | string;
  insuranceUsd: number | string;
  logisticsUsd: number | string;
  logisticsRub: number | string;
  grandTotalRub: number | string;
}

function fromBackend(calc: CommercialProposalCalculationOutput): CalcValues {
  return calc;
}

function fromPreview(calc: CommercialProposalLiveCalc): CalcValues {
  return calc;
}

export function CommercialProposalCalcPanel({
  preview,
  backend,
  language,
}: {
  preview?: CommercialProposalLiveCalc;
  backend?: CommercialProposalCalculationOutput;
  language: AppLocale;
}) {
  const { t } = useTranslation('commercialProposals');
  const values = backend ? fromBackend(backend) : preview ? fromPreview(preview) : null;
  if (!values) return null;

  return (
    <div
      data-testid="commercial-proposals-calc-panel"
      style={{
        marginTop: 18,
        padding: '14px 16px',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--fg-3)',
          marginBottom: 10,
        }}
      >
        {t('calc.title')}
      </div>
      <Row label={t('calc.serviceCny')} value={formatCny(values.serviceCny, language)} />
      <Row label={t('calc.totalCny')} value={formatCny(values.totalCny, language)} strong />
      <Row label={t('calc.transportUsd')} value={formatUsd(values.transportUsd, language)} />
      <Row label={t('calc.insuranceUsd')} value={formatUsd(values.insuranceUsd, language)} />
      <Row label={t('calc.logisticsUsd')} value={formatUsd(values.logisticsUsd, language)} />
      <Row label={t('calc.buyRub')} value={formatRub(values.buyRub, language)} strong />
      <Row label={t('calc.logisticsRub')} value={formatRub(values.logisticsRub, language)} strong />
      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid var(--border-1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--fg-2)', fontWeight: 600 }}>
          {t('calc.grandTotal')}
        </span>
        <span
          data-testid="commercial-proposals-calc-grand"
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {formatRub(values.grandTotalRub, language)}
        </span>
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-3)' }}>
        {backend ? t('calc.backendNote') : t('calc.previewNote')}
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        padding: '3px 0',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          color: 'var(--fg-1)',
          fontWeight: strong ? 600 : 400,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );
}
